import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
};

// Notifications
export const notificationsApi = {
  list: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (notificationIds) => api.post('/notifications/mark-read', { notification_ids: notificationIds }),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Users (Admin only)
export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  updateRole: (userId, data) => api.put(`/users/${userId}/role`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, newPassword) => api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  updateModules: (userId, modules) => api.put(`/admin/users/${userId}/modules`, { modules }),
  toggleActive: (userId, isActive) => api.put(`/admin/users/${userId}/toggle-active`, { is_active: isActive }),
};

// My Units (Self-service)
export const myUnitsApi = {
  list: () => api.get('/my-units'),
  add: (unitId) => api.post('/my-units', { unit_id: unitId }),
  remove: (unitId) => api.delete(`/my-units/${unitId}`),
};

// Tenant
export const tenantApi = {
  get: () => api.get('/tenant'),
  update: (data) => api.put('/tenant', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tenant/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadSignature: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tenant/signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => api.delete('/tenant/logo'),
  deleteSignature: () => api.delete('/tenant/signature'),
};

// Buildings
export const buildingsApi = {
  list: () => api.get('/buildings'),
  listWithStats: () => api.get('/buildings/with-stats'),
  withStats: () => api.get('/buildings/with-stats'),
  get: (id) => api.get(`/buildings/${id}`),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`),
  exportSettlements: (id) => api.get(`/buildings/${id}/settlements/export`, { responseType: 'blob' }),
  getChartConfig: (id) => api.get(`/buildings/${id}/chart-config`),
  updateChartConfig: (id, data) => api.put(`/buildings/${id}/chart-config`, data),
};

// Units
export const unitsApi = {
  list: (buildingId) => api.get('/units', { params: { building_id: buildingId } }),
  get: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
  bulkImport: (buildingId, filterType = 'apartments') => api.post('/units/bulk-import', { 
    building_id: buildingId, 
    filter_type: filterType 
  }),
  // Unit sensors (temperature, water, gas)
  assignSensor: (unitId, sensor) => api.post(`/units/${unitId}/sensors`, sensor),
  removeSensor: (unitId, sensorUuid) => api.delete(`/units/${unitId}/sensors/${sensorUuid}`),
  getSensorsLive: (unitId) => api.get(`/units/${unitId}/sensors/live`),
  getSensorHistory: (unitId, days = 7) => api.get(`/units/${unitId}/sensors/history`, { params: { days } }),
};

// Renters
export const rentersApi = {
  list: (unitId) => api.get('/renters', { params: { unit_id: unitId } }),
  get: (id) => api.get(`/renters/${id}`),
  create: (data) => api.post('/renters', data),
  update: (id, data) => api.put(`/renters/${id}`, data),
  delete: (id) => api.delete(`/renters/${id}`),
};

// System (Reading Server Configuration)
export const systemApi = {
  getConfig: (buildingId) => api.get('/system/config', { params: buildingId ? { building_id: buildingId } : {} }),
  getAllConfigs: () => api.get('/system/configs'),
  saveConfig: (data) => api.post('/system/config', data),
  deleteConfig: (configId) => api.delete(`/system/config/${configId}`),
  updateSyncSettings: (configId, syncInterval, syncEnabled) => api.patch(`/system/config/${configId}/sync-settings`, null, { 
    params: { sync_interval_minutes: syncInterval, sync_enabled: syncEnabled } 
  }),
  sync: (buildingId) => api.post('/system/sync', null, { params: buildingId ? { building_id: buildingId } : {} }),
  status: (buildingId) => api.get('/system/status', { params: buildingId ? { building_id: buildingId } : {} }),
  statusQuick: (buildingId) => api.get('/system/status/quick', { params: buildingId ? { building_id: buildingId } : {} }),
  test: () => api.post('/system/test'),
  getMeters: (buildingId) => api.get('/system/meters', { params: buildingId ? { building_id: buildingId } : {} }),
  // Discovery Wizard
  discover: (buildingId) => api.post(`/system/discover/${buildingId}`),
  applyDiscovery: (buildingId, data) => api.post(`/system/apply-discovery/${buildingId}`, data),
};
// Backward compatible alias
export const loxoneApi = systemApi;

// FVE (Photovoltaics)
export const fveApi = {
  buildings: () => api.get('/fve/buildings'),
  status: (buildingId) => api.get('/fve/status', { params: buildingId ? { building_id: buildingId } : {} }),
  allMeters: (buildingId) => api.get('/fve/all-meters', { params: buildingId ? { building_id: buildingId } : {} }),
  savings: (period = 'month', buildingId) => api.get('/fve/savings', { params: { period, ...(buildingId ? { building_id: buildingId } : {}) } }),
  sync: (buildingId) => api.post('/fve/sync', null, { params: buildingId ? { building_id: buildingId } : {} }),
  history: (days = 30) => api.get('/fve/history', { params: { days } }),
  battery: (buildingId) => api.get('/fve/battery', { params: buildingId ? { building_id: buildingId } : {} }),
  prediction: (buildingId) => api.get('/fve/prediction', { params: buildingId ? { building_id: buildingId } : {} }),
  monthlyTrends: (months = 6, buildingId) => api.get('/energy/monthly-trends', { params: { months, ...(buildingId ? { building_id: buildingId } : {}) } }),
  dailyEnergy: (buildingId, days = 30) => api.get('/energy/daily', { params: { building_id: buildingId, days } }),
};

// Readings
export const readingsApi = {
  list: (unitId, startDate, endDate) => api.get('/readings', { 
    params: { unit_id: unitId, start_date: startDate, end_date: endDate } 
  }),
  create: (data) => api.post('/readings', data),
  chartData: (unitId, months) => api.get(`/readings/unit/${unitId}/chart`, { params: { months } }),
  batchChartData: (unitIds, months = 3) => api.post('/readings/batch', unitIds, { params: { months } }),
};

// Tariffs
export const tariffsApi = {
  list: () => api.get('/tariffs'),
  get: () => api.get('/tariffs/current'),
  create: (data) => api.post('/tariffs', data),
  update: (id, data) => api.put(`/tariffs/${id}`, data),
};

// Settlements
export const settlementsApi = {
  list: (params) => api.get('/settlements', { params }),
  get: (id) => api.get(`/settlements/${id}`),
  generate: (data) => api.post('/settlements/generate', data),
  annualSettlement: (data) => api.post('/settlements/annual-settlement', data),
  autoGenerate: () => api.post('/settlements/auto-generate'),
  issue: (id) => api.put(`/settlements/${id}/issue`),
  updateStatus: (id, status) => api.put(`/settlements/${id}/status`, null, { params: { status } }),
  delete: (id) => api.delete(`/settlements/${id}`),
  bulkDelete: (ids) => api.post('/settlements/bulk-delete', { ids }),
  bulkIssue: (ids) => api.post('/settlements/bulk-issue', { ids }),
  bulkDownload: (ids) => api.post('/settlements/bulk-download', { ids }, { responseType: 'blob' }),
  getPdfUrl: (id) => `${API}/settlements/${id}/pdf`,
  downloadPdf: (id) => api.get(`/settlements/${id}/pdf`, { responseType: 'blob' }),
  forUnit: (unitId) => api.get(`/units/${unitId}/settlements`),
};

// Payments
export const paymentsApi = {
  list: (matched) => api.get('/payments', { params: { matched } }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payments/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  match: (paymentId, settlementId) => api.put(`/payments/${paymentId}/match/${settlementId}`),
};

// Stripe Payments
export const stripeApi = {
  // Create checkout session for settlement payment
  checkoutSettlement: (settlementId) => api.post('/stripe/checkout/settlement', {
    settlement_id: settlementId,
    origin_url: window.location.origin,
  }),
  // Create checkout session for settlement payment with Connect (destination charge)
  checkoutSettlementConnect: (settlementId) => api.post('/stripe/connect/checkout/settlement', {
    settlement_id: settlementId,
    origin_url: window.location.origin,
  }),
  // Create checkout session for deposit payment
  checkoutDeposit: (renterId, amount) => api.post('/stripe/checkout/deposit', {
    renter_id: renterId,
    amount: amount,
    origin_url: window.location.origin,
  }),
  // Get checkout session status
  getStatus: (sessionId) => api.get(`/stripe/checkout/status/${sessionId}`),
  // List all transactions
  listTransactions: () => api.get('/stripe/transactions'),
  
  // Stripe Connect
  connect: {
    createAccount: (buildingId) => api.post('/stripe/connect/create-account', {
      building_id: buildingId,
      origin_url: window.location.origin,
    }),
    getStatus: (buildingId) => api.get(`/stripe/connect/status/${buildingId}`),
    disconnect: (buildingId) => api.delete(`/stripe/connect/disconnect/${buildingId}`),
  },
  
  // Platform config (admin only)
  platform: {
    getConfig: () => api.get('/stripe/platform/config'),
    saveConfig: (secretKey, publishableKey) => api.post('/stripe/platform/config', {
      secret_key: secretKey,
      publishable_key: publishableKey,
    }),
    getStats: () => api.get('/stripe/platform/stats'),
  },
};

// Dashboard
export const dashboardApi = {
  overview: (buildingId) => api.get('/dashboard/overview', { params: { building_id: buildingId } }),
  stats: () => api.get('/dashboard/stats'),
  trends: (months, buildingId) => api.get('/dashboard/trends', { params: { months, building_id: buildingId } }),
  buildingBreakdown: (buildingId, month, year) => api.get(`/dashboard/building/${buildingId}/breakdown`, { params: { month, year } }),
  platformStats: () => api.get('/dashboard/platform-stats'),
  getConsumptionAnalysis: (buildingId, period = 'month') => api.get(`/dashboard/building/${buildingId}/consumption-analysis`, { params: { period } }),
  v2: (period = 'month', buildingId, offset = 0) => api.get('/dashboard/v2', { params: { period, offset, ...(buildingId ? { building_id: buildingId } : {}) } }),
  chartData: (period = 'month', buildingId, offset = 0) => api.get('/dashboard/chart-data', { params: { period, offset, ...(buildingId ? { building_id: buildingId } : {}) } }),
  batch: (period = 'month', buildingId, offset = 0) => api.get('/dashboard/batch', { params: { period, offset, ...(buildingId ? { building_id: buildingId } : {}) } }),
  unitChart: (unitId, days = 7, mode = 'sync') => api.get('/dashboard/unit-chart', { params: { unit_id: unitId, days, mode } }),
  energyChart: (buildingId, granularity = 'day', days = 30) => api.get('/dashboard/energy-chart', { params: { building_id: buildingId, granularity, days } }),
};

// Seed
export const seedApi = {
  demo: () => api.post('/seed/demo'),
};

// Banking (Open Banking / PSD2)
export const bankingApi = {
  // Institutions
  getInstitutions: (country = 'CZ') => api.get('/bank/institutions', { params: { country } }),
  
  // Connections
  connect: (buildingId, institutionId, provider = 'fio') => api.post('/bank/connect', {
    building_id: buildingId,
    institution_id: institutionId,
    provider: provider,
  }),
  connectFio: (buildingId, token) => api.post('/bank/fio/connect', {
    building_id: buildingId,
    token: token,
  }),
  getConnections: (buildingId) => api.get('/bank/connections', { params: { building_id: buildingId } }),
  getConnectionStatus: (buildingId) => api.get(`/bank/connection/${buildingId}`),
  disconnect: (connectionId) => api.delete(`/bank/connection/${connectionId}`),
  
  // Transactions
  getTransactions: (buildingId, status, limit = 100, offset = 0) => api.get('/bank/transactions', {
    params: { building_id: buildingId, status, limit, offset }
  }),
  sync: (buildingId, daysBack = 30) => api.post('/bank/sync', {
    building_id: buildingId,
    days_back: daysBack,
  }),
  
  // Matching
  match: (transactionId, settlementId) => api.post('/bank/match', {
    transaction_id: transactionId,
    settlement_id: settlementId,
  }),
  unmatch: (transactionId) => api.post(`/bank/unmatch/${transactionId}`),
  ignore: (transactionId, reason) => api.post(`/bank/ignore/${transactionId}`, null, {
    params: { reason }
  }),
  
  // Delete transactions
  deleteTransaction: (transactionId) => api.delete(`/bank/transaction/${transactionId}`),
  bulkDeleteTransactions: (buildingId, status = 'ignored') => api.delete('/bank/transactions/bulk', {
    params: { building_id: buildingId, status }
  }),
  
  // CSV Import
  importCsv: (buildingId, file, bankFormat = 'auto') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/bank/import-csv/${buildingId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { bank_format: bankFormat }
    });
  },
  
  // Stats
  getStats: (buildingId) => api.get('/bank/stats', { params: { building_id: buildingId } }),
};

// Alerts
export const alertsApi = {
  list: (buildingId, status = 'active') => api.get('/alerts', { params: { building_id: buildingId, status } }),
  unreadCount: () => api.get('/alerts/unread-count'),
  acknowledge: (alertId) => api.put(`/alerts/${alertId}/acknowledge`),
  resolve: (alertId) => api.put(`/alerts/${alertId}/resolve`),
  check: (buildingId) => api.post(`/alerts/check/${buildingId}`),
  // Rules
  getRules: (buildingId) => api.get('/alerts/rules', { params: { building_id: buildingId } }),
  createRule: (data) => api.post('/alerts/rules', data),
  deleteRule: (ruleId) => api.delete(`/alerts/rules/${ruleId}`),
};

// Anomalies
export const anomaliesApi = {
  list: (buildingId, status = 'pending') => api.get('/anomalies', { params: { building_id: buildingId, status } }),
  detect: (buildingId, days = 30) => api.post(`/anomalies/detect/${buildingId}`, null, { params: { days } }),
  resolve: (anomalyId, resolution, notes, newStartKwh) => api.put(`/anomalies/${anomalyId}/resolve`, {
    resolution,
    notes,
    new_start_kwh: newStartKwh
  }),
};

// Notes
export const notesApi = {
  list: (buildingId, unitId) => api.get('/notes', { params: { building_id: buildingId, unit_id: unitId } }),
  create: (data) => api.post('/notes', data),
  update: (noteId, data) => api.put(`/notes/${noteId}`, data),
  delete: (noteId) => api.delete(`/notes/${noteId}`),
};

// Data validation & Admin
export const adminApi = {
  validate: (buildingId) => api.get(`/data/validate/${buildingId}`),
  deleteData: (data) => api.post('/admin/data/delete', data),
  rebuildSummary: (buildingId, dateFrom, dateTo) => api.post(`/admin/data/rebuild-summary/${buildingId}`, null, {
    params: { date_from: dateFrom, date_to: dateTo }
  }),
  getAuditLog: (action, entityType, limit = 100) => api.get('/admin/data/audit-log', { params: { action, entity_type: entityType, limit } }),
  
  // System config
  getSystemConfig: () => api.get('/admin/system-config'),
  updateEnv: (key, value) => api.post(`/admin/update-env?key=${encodeURIComponent(key)}&value=${encodeURIComponent(value)}`),
  getDemoLeads: (limit = 100) => api.get('/admin/demo-leads', { params: { limit } }),
  
  // Licensing
  getLicenses: () => api.get('/admin/licenses'),
  updateLicense: (tenantId, data) => api.put(`/admin/licenses/${tenantId}`, data),
  updateLicenseConfig: (config) => api.put('/admin/license-config', config),
};

// License API
export const licenseApi = {
  getMy: () => api.get('/license/my'),
  getPricing: () => api.get('/license/pricing'),
  accessStatus: () => api.get('/license/access-status'),
  createSubscription: (originUrl) => api.post('/license/create-subscription', { origin_url: originUrl }),
  checkoutStatus: (sessionId) => api.get(`/license/checkout-status/${sessionId}`),
  billingHistory: () => api.get('/license/billing-history'),
  deleteBillingHistory: () => api.delete('/license/billing-history'),
  kwhUsage: () => api.get('/license/kwh-usage'),
  cancel: () => api.post('/license/cancel'),
  reactivate: () => api.post('/license/reactivate'),
  updateSubscription: () => api.post('/license/update-subscription'),
  invoiceUrl: (sessionId) => {
    const token = localStorage.getItem('token');
    return `${API}/license/invoice/${sessionId}?token=${token}`;
  },
};

// Map API
export const mapApi = {
  getBuildings: () => api.get('/map/buildings'),
};

// Anomaly API
export const anomalyDetectionApi = {
  getUnitAnomalies: (unitId, days = 30) => api.get(`/units/${unitId}/anomalies`, { params: { days } }),
  deleteReadings: (unitId, dateFrom, dateTo) => api.delete(`/units/${unitId}/readings`, { params: { date_from: dateFrom, date_to: dateTo } }),
  resetReadings: (unitId) => api.post(`/units/${unitId}/reset-readings`),
};

// AI Agent API (Admin only)
export const agentApi = {
  sendCommand: (message, sessionId) => api.post('/agent/command', { message, session_id: sessionId }),
};

// API Keys Management (Admin only)
export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  revoke: (keyId) => api.delete(`/api-keys/${keyId}`),
};

// Webhooks Management (Admin only)
export const webhooksApi = {
  listEvents: () => api.get('/webhooks/events'),
  list: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  delete: (webhookId) => api.delete(`/webhooks/${webhookId}`),
  getDeliveries: (webhookId, limit = 50) => api.get(`/webhooks/${webhookId}/deliveries`, { params: { limit } }),
  test: (webhookId) => api.post(`/webhooks/${webhookId}/test`),
};

// Admin Studio API
export const adminStudioApi = {
  // Config Engine
  getSchemas: () => api.get('/admin-studio/configs/schemas'),
  getEffectiveConfig: (category) => api.get(`/admin-studio/configs/effective/${category}`),
  listConfigs: (category) => api.get('/admin-studio/configs', { params: category ? { category } : {} }),
  createDraft: (category, data) => api.post('/admin-studio/configs/draft', { category, data }),
  publishConfig: (configId) => api.post('/admin-studio/configs/publish', { config_id: configId }),
  rollbackConfig: (category, targetVersion) => api.post('/admin-studio/configs/rollback', { category, target_version: targetVersion }),
  diffConfigs: (configIdA, configIdB) => api.post('/admin-studio/configs/diff', { config_id_a: configIdA, config_id_b: configIdB }),

  // Feature Flags
  getFeatureFlags: () => api.get('/admin-studio/feature-flags'),
  updateFeatureFlags: (flags) => api.put('/admin-studio/feature-flags', { flags }),
  checkFeatureFlag: (flagName) => api.get(`/admin-studio/feature-flags/check/${flagName}`),

  // Audit Log
  queryAuditLog: (params) => api.post('/admin-studio/audit-log/query', params),
  exportAuditLog: (fromDate, toDate) => api.get('/admin-studio/audit-log/export', { params: { from_date: fromDate, to_date: toDate }, responseType: 'blob' }),

  // Data Lineage
  listMetrics: () => api.get('/admin-studio/lineage/metrics'),
  explainMetric: (metricName, buildingId, unitId) => api.post('/admin-studio/lineage/explain', { metric_name: metricName, building_id: buildingId, unit_id: unitId }),
  getSettlementLineage: (settlementId) => api.get(`/admin-studio/lineage/settlement/${settlementId}`),

  // Dry-Run
  dryRunSettlement: (unitId, month, year, initialStateKwh) => api.post('/admin-studio/dry-run/settlement', { unit_id: unitId, month, year, initial_state_kwh: initialStateKwh }),
  dryRunBulkSettlement: (buildingId, month, year) => api.post('/admin-studio/dry-run/bulk-settlement', { building_id: buildingId, month, year }),
  dryRunConfigChange: (category, newConfig, sampleUnitId) => api.post('/admin-studio/dry-run/config-change', { category, new_config: newConfig, sample_unit_id: sampleUnitId }),

  // Sync Management
  getAllSyncStatus: () => api.get('/admin-studio/sync/status'),
  getSyncStatus: (buildingId) => api.get(`/admin-studio/sync/status/${buildingId}`),
  triggerSync: (buildingId) => api.post(`/admin-studio/sync/trigger/${buildingId}`),
  updateSyncInterval: (buildingId, intervalMinutes) => api.put(`/admin-studio/sync/interval/${buildingId}`, { interval_minutes: intervalMinutes }),
  toggleSync: (buildingId, enabled) => api.put(`/admin-studio/sync/toggle/${buildingId}`, { enabled }),
  clearSyncErrors: (buildingId) => api.delete(`/admin-studio/sync/errors/${buildingId}`),

  // Anomaly Management
  getAnomalyRules: () => api.get('/admin-studio/anomaly-rules'),
  saveAnomalyRules: (rules) => api.put('/admin-studio/anomaly-rules', { rules }),
  createManualAnomaly: (data) => api.post('/admin-studio/anomalies/manual', data),
  listAnomalies: (params) => api.get('/admin-studio/anomalies', { params }),
  resolveAnomaly: (id, note) => api.put(`/admin-studio/anomalies/${id}/resolve`, { resolution_note: note }),
  deleteAnomaly: (id) => api.delete(`/admin-studio/anomalies/${id}`),

  // Data Catalog
  getDataCatalog: () => api.get('/admin-studio/data-catalog'),
};

// Energy Flow Dashboard API
export const energyFlowApi = {
  getLayout: (buildingId) => api.get(`/energy-flow/layout/${buildingId}`),
  saveLayout: (buildingId, data) => api.put(`/energy-flow/layout/${buildingId}`, data),
  getData: (buildingId, view = 'current') => api.get(`/energy-flow/data/${buildingId}`, { params: { view } }),
  getAvailableModules: (buildingId) => api.get(`/energy-flow/available-modules/${buildingId}`),
};

// Meter Dashboard API
export const metersApi = {
  getBuilding: (buildingId) => api.get(`/meters/building/${buildingId}`),
  getUnit: (unitId) => api.get(`/meters/unit/${unitId}`),
};

// Sync Management API
export const syncApi = {
  getAllStatus: () => api.get('/sync/status'),
  getStatus: (buildingId) => api.get(`/sync/status/${buildingId}`),
  trigger: (buildingId) => api.post(`/sync/trigger/${buildingId}`),
  retry: (buildingId) => api.post(`/sync/retry/${buildingId}`),
  updateInterval: (buildingId, minutes) => api.put(`/sync/interval/${buildingId}`, { interval_minutes: minutes }),
  toggle: (buildingId, enabled) => api.put(`/sync/toggle/${buildingId}`, { enabled }),
  clearErrors: (buildingId) => api.delete(`/sync/errors/${buildingId}`),
  getHistory: (buildingId, limit = 20) => api.get(`/sync/history/${buildingId}`, { params: { limit } }),
};

// Page Layout API
export const layoutsApi = {
  getCatalog: () => api.get('/layouts/catalog'),
  getPages: () => api.get('/layouts/pages'),
  getLayout: (page, buildingId) => api.get(`/layouts/${page}`, { params: buildingId ? { building_id: buildingId } : {} }),
  saveLayout: (page, data) => api.put(`/layouts/${page}`, data),
  resetLayout: (page, buildingId) => api.post(`/layouts/${page}/reset`, buildingId ? { building_id: buildingId } : {}),
};

// PDF Templates API
export const pdfTemplatesApi = {
  list: () => api.get('/pdf-templates'),
  get: (id) => api.get(`/pdf-templates/${id}`),
  create: (data) => api.post('/pdf-templates', data),
  update: (id, data) => api.put(`/pdf-templates/${id}`, data),
  delete: (id) => api.delete(`/pdf-templates/${id}`),
  activate: (id) => api.put(`/pdf-templates/${id}/activate`),
  getActiveConfig: () => api.get('/pdf-templates/active/config'),
};

export default api;
